/**
 * UploadLinksSection - Admin component for managing secure upload links
 */
import React, { useState, useEffect } from 'react';
import { uploadLinksApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';
import { toast } from 'sonner';
import { 
    Link, 
    Plus, 
    Copy, 
    Check, 
    Trash2, 
    Lock, 
    Clock, 
    FileUp,
    ExternalLink,
    AlertCircle,
    Mail,
    Send,
    ChevronDown
} from 'lucide-react';

// Coarse upload-link scopes presented in the UI. Each scope expands to the
// granular `allowed_categories` list the backend already validates against,
// so the storage shape is unchanged. Keeping the UI binary (Master / Artwork)
// avoids customers having to know about side_a / side_b / tracklist plumbing.
const getCoarseScopesForProductType = (productType) => {
    if (productType === 'vinyl') {
        return [
            { value: 'master', label: 'Master', expandsTo: ['audio_side_a', 'audio_side_b', 'tracklist'] },
            { value: 'artwork', label: 'Artwork', expandsTo: ['artwork'] },
        ];
    }
    // CD / DVD / Blu-ray (or unknown — same coarse choices)
    return [
        { value: 'master', label: 'Master', expandsTo: ['master', 'tracklist'] },
        { value: 'artwork', label: 'Artwork', expandsTo: ['artwork'] },
    ];
};

// Helper to display the upload-link categories using the coarse "Master /
// Artwork" labels in the listing, instead of leaking the granular backend
// category names (audio_side_a, tracklist, ...) to the user.
const displayCategoryLabels = (allowed_categories, productType) => {
    const scopes = getCoarseScopesForProductType(productType);
    const labels = [];
    for (const scope of scopes) {
        if (scope.expandsTo.some((c) => allowed_categories.includes(c))) {
            labels.push(scope.label);
        }
    }
    // Fallback: if the link contains legacy categories we don't recognise,
    // surface them verbatim so admins still see something.
    if (labels.length === 0) return allowed_categories.join(', ');
    return labels.join(', ');
};

export function UploadLinksSection({ orderId, productType, hideIntro = false, compact = false }) {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [copiedToken, setCopiedToken] = useState(null);
    const [revoking, setRevoking] = useState(null);
    
    // Coarse UI scopes (Master / Artwork). Selecting a scope toggles all the
    // granular backend categories it represents.
    const COARSE_SCOPES = getCoarseScopesForProductType(productType);

    // Default scope for new links: Master + Artwork pre-selected so users only
    // have to deselect what they don't want to share.
    const getDefaultCategories = () => {
        return COARSE_SCOPES.flatMap((s) => s.expandsTo);
    };
    
    const [form, setForm] = useState({
        name: '',
        allowed_categories: getDefaultCategories(),
        expires_at: '',
        password: '',
        max_files: '',
        notes: '',
        recipient_email: '',
        send_email: false
    });
    
    const fetchLinks = async () => {
        try {
            const response = await uploadLinksApi.list(orderId);
            setLinks(response.data);
        } catch (error) {
            console.error('Failed to fetch upload links:', error);
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchLinks();
    }, [orderId]);
    
    const handleCreate = async () => {
        if (!form.name.trim()) {
            toast.error('Please enter a name for the upload link');
            return;
        }
        
        if (form.allowed_categories.length === 0) {
            toast.error('Please select at least one file category');
            return;
        }
        
        if (form.send_email && !form.recipient_email.trim()) {
            toast.error('Please enter a recipient email address');
            return;
        }
        
        setIsCreating(true);
        try {
            const data = {
                name: form.name.trim(),
                allowed_categories: form.allowed_categories,
                expires_at: form.expires_at || null,
                password: form.password || null,
                max_files: form.max_files ? parseInt(form.max_files) : null,
                notes: form.notes || null,
                recipient_email: form.recipient_email.trim() || null,
                send_email: form.send_email
            };
            
            const response = await uploadLinksApi.create(orderId, data);
            
            if (form.send_email && response.data?.email_sent) {
                toast.success('Upload link created and email sent');
            } else if (form.send_email) {
                toast.success('Upload link created (email sending failed)');
            } else {
                toast.success('Upload link created successfully');
            }
            
            setIsCreateDialogOpen(false);
            setForm({
                name: '',
                allowed_categories: getDefaultCategories(),
                expires_at: '',
                password: '',
                max_files: '',
                notes: '',
                recipient_email: '',
                send_email: false
            });
            fetchLinks();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create upload link');
        } finally {
            setIsCreating(false);
        }
    };
    
    const handleRevoke = async (linkId) => {
        setRevoking(linkId);
        try {
            await uploadLinksApi.revoke(orderId, linkId);
            toast.success('Upload link revoked');
            fetchLinks();
        } catch (error) {
            toast.error('Failed to revoke upload link');
        } finally {
            setRevoking(null);
        }
    };
    
    const copyLink = (token) => {
        const url = `${window.location.origin}/upload/${token}`;
        navigator.clipboard.writeText(url);
        setCopiedToken(token);
        toast.success('Link copied to clipboard');
        setTimeout(() => setCopiedToken(null), 2000);
    };
    
    // Toggle a coarse UI scope (Master / Artwork). Adds or removes ALL the
    // granular backend categories that scope expands into so the backend's
    // existing per-category access checks keep working unchanged.
    const toggleScope = (scope) => {
        setForm(prev => {
            const isSelected = scope.expandsTo.every(c => prev.allowed_categories.includes(c));
            const allowed_categories = isSelected
                ? prev.allowed_categories.filter(c => !scope.expandsTo.includes(c))
                : Array.from(new Set([...prev.allowed_categories, ...scope.expandsTo]));
            return { ...prev, allowed_categories };
        });
    };
    
    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        return new Date(dateStr).toLocaleString();
    };
    
    const now = new Date();
    const activeCount = links.filter(
        (l) => l.is_active && (!l.expires_at || new Date(l.expires_at) >= now)
    ).length;

    const linksTable = (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs">Name</TableHead>
                            <TableHead className="text-xs">Categories</TableHead>
                            <TableHead className="text-xs">Uploads</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs w-20"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {links.map(link => (
                            <TableRow key={link.id} className={!link.is_active ? 'opacity-50' : ''}>
                                <TableCell className="text-xs">
                                    <div className="flex items-center gap-2">
                                        <Link className="h-3.5 w-3.5 text-violet-500" />
                                        <span className="font-medium">{link.name}</span>
                                        {link.has_password && (
                                            <Lock className="h-3 w-3 text-amber-500" title="Password protected" />
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs text-slate-500">
                                    {displayCategoryLabels(link.allowed_categories, productType)}
                                </TableCell>
                                <TableCell className="text-xs">
                                    <span className="font-mono">
                                        {link.upload_count}
                                        {link.max_files && `/${link.max_files}`}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    {!link.is_active ? (
                                        <span className="text-xs text-red-600 flex items-center gap-1">
                                            <AlertCircle className="h-3 w-3" />
                                            Revoked
                                        </span>
                                    ) : link.expires_at && new Date(link.expires_at) < new Date() ? (
                                        <span className="text-xs text-slate-400 flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            Expired
                                        </span>
                                    ) : (
                                        <span className="text-xs text-green-600 flex items-center gap-1">
                                            <Check className="h-3 w-3" />
                                            Active
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-1">
                                        {link.is_active && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onClick={() => copyLink(link.token)}
                                                    title="Copy link"
                                                    data-testid={`copy-link-${link.id}`}
                                                >
                                                    {copiedToken === link.token ? (
                                                        <Check className="h-3.5 w-3.5 text-green-500" />
                                                    ) : (
                                                        <Copy className="h-3.5 w-3.5 text-slate-400" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onClick={() => window.open(`/upload/${link.token}`, '_blank')}
                                                    title="Open link"
                                                >
                                                    <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => handleRevoke(link.id)}
                                                    disabled={revoking === link.id}
                                                    title="Revoke link"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
    );

    const createDialog = (
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="flex items-center gap-2">
                            <Link className="h-5 w-5 text-violet-500" />
                            Create Upload Link
                        </DialogTitle>
                        <DialogDescription>
                            Create a secure link for external partners to upload files.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-y-auto pr-2 -mr-2">
                        <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="link-name">Link Name *</Label>
                            <Input
                                id="link-name"
                                placeholder="e.g., Post House Upload, Mastering Studio"
                                value={form.name}
                                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                data-testid="link-name-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Allowed File Categories *</Label>
                            <div className="flex flex-wrap gap-2">
                                {COARSE_SCOPES.map(scope => {
                                    const isSelected = scope.expandsTo.every(
                                        c => form.allowed_categories.includes(c)
                                    );
                                    return (
                                        <Button
                                            key={scope.value}
                                            type="button"
                                            variant={isSelected ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => toggleScope(scope)}
                                            className="h-7 text-xs"
                                            data-testid={`scope-toggle-${scope.value}`}
                                        >
                                            {scope.label}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="expires-at">Expires At (optional)</Label>
                            <Input
                                id="expires-at"
                                type="datetime-local"
                                value={form.expires_at}
                                onChange={(e) => setForm(prev => ({ ...prev, expires_at: e.target.value }))}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="password">Password Protection (optional)</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Leave empty for no password"
                                value={form.password}
                                onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="max-files">Max Files (optional)</Label>
                            <Input
                                id="max-files"
                                type="number"
                                placeholder="Leave empty for unlimited"
                                value={form.max_files}
                                onChange={(e) => setForm(prev => ({ ...prev, max_files: e.target.value }))}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="notes">Instructions (optional)</Label>
                            <Textarea
                                id="notes"
                                placeholder="Add instructions for the uploader..."
                                value={form.notes}
                                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                                rows={2}
                            />
                        </div>
                        
                        {/* Email Section */}
                        <div className="border-t pt-4 mt-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Mail className="h-4 w-4 text-slate-400" />
                                <Label className="text-sm font-medium">Send Link by Email</Label>
                            </div>
                            
                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label htmlFor="recipient-email">Recipient Email</Label>
                                    <Input
                                        id="recipient-email"
                                        type="email"
                                        placeholder="partner@example.com"
                                        value={form.recipient_email}
                                        onChange={(e) => setForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                                        data-testid="recipient-email-input"
                                    />
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="send-email"
                                            checked={form.send_email}
                                            onCheckedChange={(checked) => setForm(prev => ({ ...prev, send_email: checked }))}
                                        />
                                        <label 
                                            htmlFor="send-email" 
                                            className="text-sm text-slate-600 cursor-pointer"
                                        >
                                            Send email notification
                                        </label>
                                    </div>
                                    {form.send_email && (
                                        <span className="text-xs text-slate-400 flex items-center gap-1">
                                            <Send className="h-3 w-3" />
                                            Will send email with link
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                    
                    <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
                        <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} disabled={isCreating} data-testid="create-link-submit">
                            {isCreating ? 'Creating...' : 'Create Link'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
    );

    if (compact) {
        return (
            <div className="space-y-3" data-testid="upload-invite-compact">
                <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                        <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-600">
                            <Link className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 leading-tight">Someone else uploading your files?</p>
                            <p className="text-xs text-slate-500 leading-snug">Create a secure link so a partner can upload directly — no account needed.</p>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => setIsCreateDialogOpen(true)}
                        className="h-8 text-xs w-full flex-shrink-0 sm:w-auto"
                        data-testid="create-upload-link-btn"
                    >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Create link
                    </Button>
                </div>

                {activeCount > 0 && (
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <button
                            type="button"
                            onClick={() => setExpanded((e) => !e)}
                            className="flex w-full items-center gap-2 text-xs font-medium text-slate-600 hover:text-slate-900"
                            data-testid="manage-upload-links-toggle"
                        >
                            <Check className="h-3.5 w-3.5 flex-shrink-0 text-emerald-500" />
                            <span className="flex-1 text-left">{activeCount} active upload link{activeCount === 1 ? '' : 's'}</span>
                            <span className="font-normal text-slate-400">{expanded ? 'Hide' : 'Manage'}</span>
                            <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                        </button>
                        {expanded && (
                            <div className="mt-3 overflow-x-auto">
                                {linksTable}
                            </div>
                        )}
                    </div>
                )}

                {createDialog}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                {hideIntro ? <span /> : (
                    <p className="text-xs text-slate-500">
                        Share secure links with external partners to upload files without needing an account.
                    </p>
                )}
                <Button
                    size="sm"
                    onClick={() => setIsCreateDialogOpen(true)}
                    className="h-7 text-xs"
                    data-testid="create-upload-link-btn"
                >
                    <Plus className="h-3 w-3 mr-1" />
                    Create Link
                </Button>
            </div>

            {loading ? (
                <div className="text-xs text-slate-400 py-4 text-center">Loading...</div>
            ) : links.length === 0 ? (
                <div className="text-xs text-slate-400 py-4 text-center border border-dashed rounded-lg">
                    No upload links created yet
                </div>
            ) : (
                linksTable
            )}

            {createDialog}
        </div>
    );
}

export default UploadLinksSection;
